import { useMemo, useState } from "react";
import { Link } from "react-router-dom";

import type { ColumnDef } from "@tanstack/react-table";
import { ArrowLeft, Edit3, Loader2, MoreHorizontal, Plus, Tags, Trash2 } from "lucide-react";

import { AdviceCategoryFormSheet } from "@/features/advice/presentation/components/AdviceCategoryFormSheet";
import { ConfirmDialog } from "@/shared/components/data/ConfirmDialog";
import { DataTable } from "@/shared/components/data/DataTable";
import { DataToolbar } from "@/shared/components/data/DataToolbar";
import { Pagination } from "@/shared/components/data/Pagination";
import { PageHeader } from "@/shared/components/layout/PageHeader";
import { Button } from "@/shared/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/shared/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import { useDebouncedValue } from "@/shared/hooks/use-debounced-value";
import { useQueryState } from "@/shared/hooks/use-query-state";
import { toast } from "@/shared/lib/toast";
import { cn } from "@/shared/lib/utils";
import {
  useDeleteAdviceCategoryMutation,
  useListAdviceCategoriesQuery,
} from "@/store/apis/adminApi.enhanced";
import type { AdviceCategory } from "@/store/apis/adminApi.enhanced";
import type { ListAdviceCategoriesApiArg } from "@/store/apis/adminApi.generated";

type Sort = NonNullable<ListAdviceCategoriesApiArg["sort"]>;

const DEFAULT_LIMIT = 25;

export function AdviceCategoriesPage() {
  const { get, set } = useQueryState();

  const search = get("q", "");
  const debouncedSearch = useDebouncedValue(search, 300);
  const sort = (get("sort", "position") || "position") as Sort;
  const offset = Number(get("offset", "0")) || 0;
  const limit = Number(get("limit", String(DEFAULT_LIMIT))) || DEFAULT_LIMIT;
  const filtersActive = debouncedSearch.trim().length > 0;

  const { data, isLoading, isFetching, isError, error, refetch } = useListAdviceCategoriesQuery({
    q: debouncedSearch || undefined,
    sort,
    offset,
    limit,
  });

  const [deleteCategory] = useDeleteAdviceCategoryMutation();

  const [sheetOpen, setSheetOpen] = useState(false);
  const [editing, setEditing] = useState<AdviceCategory | null>(null);

  const openCreate = () => {
    setEditing(null);
    setSheetOpen(true);
  };
  const openEdit = (cat: AdviceCategory) => {
    setEditing(cat);
    setSheetOpen(true);
  };

  const handleDelete = async (cat: AdviceCategory) => {
    try {
      await deleteCategory({ slug: cat.slug }).unwrap();
      toast.success("Categoría eliminada");
    } catch (err) {
      toast.error(err);
      throw err;
    }
  };

  const columns = useMemo<ColumnDef<AdviceCategory>[]>(
    () => [
      {
        id: "label",
        header: "Nombre",
        cell: ({ row }) => (
          <span className="inline-flex items-center gap-2.5 text-[13.5px] text-foreground">
            <span
              aria-hidden
              className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-muted/60 text-base"
            >
              {row.original.emoji || "·"}
            </span>
            <span className="font-medium">{row.original.label}</span>
          </span>
        ),
      },
      {
        id: "slug",
        header: "Slug",
        cell: ({ row }) => (
          <code className="rounded bg-muted/60 px-1.5 py-0.5 font-mono text-[11.5px] text-muted-foreground">
            {row.original.slug}
          </code>
        ),
      },
      {
        id: "position",
        header: "Pos.",
        cell: ({ row }) => (
          <span className="font-mono text-[12px] tabular-nums text-muted-foreground">
            {row.original.position}
          </span>
        ),
      },
      {
        id: "actions",
        header: () => <span className="sr-only">Acciones</span>,
        cell: ({ row }) => (
          <div className="flex justify-end">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8">
                  <MoreHorizontal className="h-4 w-4" />
                  <span className="sr-only">Acciones</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onSelect={() => openEdit(row.original)}>
                  <Edit3 className="h-3.5 w-3.5" />
                  Editar
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <ConfirmDialog
                  trigger={
                    <DropdownMenuItem
                      onSelect={(e) => e.preventDefault()}
                      className="text-destructive focus:text-destructive"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                      Eliminar
                    </DropdownMenuItem>
                  }
                  title="Eliminar categoría"
                  description={
                    <>
                      Se eliminará la categoría{" "}
                      <code className="rounded bg-muted px-1 py-0.5 font-mono text-[11.5px]">
                        {row.original.slug}
                      </code>
                      . Si tiene consejos asignados, la operación fallará — primero reasigna esos
                      consejos a otra categoría.
                    </>
                  }
                  confirmLabel="Sí, eliminar"
                  onConfirm={() => handleDelete(row.original)}
                />
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  return (
    <>
      <PageHeader
        kicker="Consejos · Categorías"
        title="Categorías de consejos"
        description="Agrupaciones temáticas que organizan el banco de consejos en la app."
        actions={
          <>
            <Button asChild variant="outline" size="sm">
              <Link to="/consejos">
                <ArrowLeft className="h-3.5 w-3.5" />
                Volver a consejos
              </Link>
            </Button>
            <Button size="sm" onClick={openCreate}>
              <Plus className="h-3.5 w-3.5" />
              Nueva categoría
            </Button>
          </>
        }
      />

      <DataToolbar
        search={search}
        onSearchChange={(next) => set({ q: next, offset: 0 })}
        searchPlaceholder="Buscar por nombre o slug…"
        filters={
          <Select value={sort ?? "position"} onValueChange={(v) => set({ sort: v })}>
            <SelectTrigger className="h-10 w-[160px] text-[13px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="position">Posición ↑</SelectItem>
              <SelectItem value="-position">Posición ↓</SelectItem>
              <SelectItem value="label">Nombre A–Z</SelectItem>
              <SelectItem value="-label">Nombre Z–A</SelectItem>
            </SelectContent>
          </Select>
        }
        actions={
          isFetching && !isLoading ? (
            <span
              className={cn(
                "inline-flex items-center gap-1.5 font-mono text-[11px] text-muted-foreground",
              )}
            >
              <Loader2 className="h-3 w-3 animate-spin" />
              Actualizando
            </span>
          ) : null
        }
      />

      <DataTable
        columns={columns}
        data={data?.items}
        loading={isLoading}
        error={isError ? error : undefined}
        onRetry={() => void refetch()}
        onRowClick={openEdit}
        getRowId={(row) => row.slug}
        filtersActive={filtersActive}
        empty={{
          icon: <Tags className="h-6 w-6" />,
          title: "Aún no hay categorías",
          description: "Crea al menos una categoría antes de agregar consejos.",
          actions: (
            <Button onClick={openCreate}>
              <Plus className="h-3.5 w-3.5" />
              Nueva categoría
            </Button>
          ),
        }}
        noResults={{
          actions: (
            <Button variant="outline" size="sm" onClick={() => set({ q: "", offset: 0 })}>
              Limpiar búsqueda
            </Button>
          ),
        }}
      />

      <Pagination
        total={data?.total ?? 0}
        limit={limit}
        offset={offset}
        onOffsetChange={(next) => set({ offset: next })}
        onLimitChange={(next) => set({ limit: next })}
      />

      <AdviceCategoryFormSheet open={sheetOpen} onOpenChange={setSheetOpen} category={editing} />
    </>
  );
}
